import { useState } from 'react';
import axios from 'axios';

/**
 * Custom hook for managing document operations
 * Handles document upload, download, and list management
 */
export const useDocumentHandlers = () => {
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [documentContext, setDocumentContext] = useState({
    entityType: null, // 'ASSEMBLY' | 'COMPONENT'
    record: null
  });
  const [documentForm, setDocumentForm] = useState({
    documentType: '',
    documentSubType: '',
    description: '',
    file: null
  });
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const [documentList, setDocumentList] = useState([]);

  const openDocumentDialog = async (entityType, record) => {
    setDocumentContext({ entityType, record });
    setDocumentForm({
      documentType: '',
      documentSubType: '',
      description: '',
      file: null
    });
    setDocumentUploadProgress(0);
    setDocumentUploading(false);
    setDocumentList([]);

    try {
      const baseUrl =
        entityType === 'ASSEMBLY'
          ? `/api/assemblies/${record._id}/documents`
          : `/api/components/${record._id}/documents`;
      const res = await axios.get(baseUrl);
      setDocumentList(res.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }

    setDocumentDialogOpen(true);
  };

  const handleDocumentUpload = async () => {
    if (!documentForm.file || !documentForm.documentType || !documentContext.record) {
      alert('Please select a file and document type');
      return;
    }

    setDocumentUploading(true);
    setDocumentUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', documentForm.file);
      formDataToSend.append('documentType', documentForm.documentType);
      if (documentForm.documentSubType) {
        formDataToSend.append('documentSubType', documentForm.documentSubType);
      }
      if (documentForm.description) {
        formDataToSend.append('description', documentForm.description);
      }

      const baseUrl =
        documentContext.entityType === 'ASSEMBLY'
          ? `/api/assemblies/${documentContext.record._id}/documents`
          : `/api/components/${documentContext.record._id}/documents`;

      await axios.post(baseUrl, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setDocumentUploadProgress(percentCompleted);
        }
      });

      const refreshed = await axios.get(baseUrl);
      setDocumentList(refreshed.data || []);

      setDocumentForm({
        documentType: '',
        documentSubType: '',
        description: '',
        file: null
      });
      alert('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.message || 'Error uploading document');
    } finally {
      setDocumentUploading(false);
      setDocumentUploadProgress(0);
    }
  };

  const handleDocumentDownload = async (docId, fileName) => {
    if (!documentContext.record) return;
    try {
      const baseUrl =
        documentContext.entityType === 'ASSEMBLY'
          ? `/api/assemblies/${documentContext.record._id}/documents/${docId}/download`
          : `/api/components/${documentContext.record._id}/documents/${docId}/download`;
      
      const response = await axios.get(baseUrl, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  return {
    documentDialogOpen,
    documentContext,
    documentForm,
    documentUploading,
    documentUploadProgress,
    documentList,
    setDocumentDialogOpen,
    setDocumentForm,
    openDocumentDialog,
    handleDocumentUpload,
    handleDocumentDownload
  };
};
